<template lang="pug">
.inventory-edit
  .title-section Editar Inventario

  form(v-if="!loaded" @submit.prevent="")
    .inventory-edit__form-row
      .input-container.arrow
        select.select(v-model="brand" autocomplete="ño" required)
          option(value="")
          option(v-for="brandOption in brands" :value="brandOption") {{ capitalize(brandOption) }}
        label.label(:class="{ show: brand }") Marca

      .inventory-edit__form-actions
        shActionButton(:isForm="true" :action="getData" text="Obtener" textRunning="Obteniendo")

  KeyboardTypingOverlay(v-if="loaded" :key="loadKey" :active="loaded" input-label="Nivel o producto" @submit="onTierSubmit")

  .inventory-edit__content(v-if="loaded")
    p.inventory-edit__hint(v-if="!visibleTierGroups.length") Escribe un nivel o producto para mostrar la información.
    .tiers-toolbar(v-if="visibleTierGroups.length")
      .tier-tag(v-for="tierGroup in visibleTierGroups" :key="`tag-${tierGroup.tier}`")
        span.tier-tag__label.uppercase {{ tierGroup.tier }}
        span.material-symbols-outlined.tier-tag__close(@click.stop="hideTier(tierGroup.tier)" title="Ocultar") close
      .tier-tag(v-if="visibleTierGroups.length >= 2")
        span.tier-tag__label.uppercase todos
        span.material-symbols-outlined.tier-tag__close(@click="hideAllTiers" title="Ocultar todos") close
    transition-group.tier-groups(name="slide-fade" tag="div")
      .tier-group(v-for="tierGroup in visibleTierGroups" :key="tierGroup.tier")
        .tier-header
          .tier-name.uppercase {{ tierGroup.tier }}
          span.material-symbols-outlined.tier-toggle(
            @click="toggleTier(tierGroup.tier)"
            title="Ocultar"
          ) visibility_off
        .tier-items
          InventoryProduct(
            v-for="item in tierGroup.items"
            :key="`${tierGroup.tier}-${item.product}-${item.size}`"
            clickable
            :reference="item.product"
            :size="item.size"
            :quantity="item.quantity"
            :brand="item.brand"
            :folder="item.folder"
            :subfolder="item.subfolder"
            :image="item.image"
            @click="openTransferModal(tierGroup.tier, item)"
          )

  InventoryTransferModal(
    :show="!!transferSelection"
    :source-tier="transferSelection ? transferSelection.tier : ''"
    :item="transferSelection ? transferSelection.item : null"
    :visible-tiers="visibleTierNames"
    :tier-groups="tierGroups"
    @close="closeTransferModal"
    @updated="onInventoryUpdated"
  )
</template>

<script>
import mForm from '@/mixins/form'
import mFilters from '@/mixins/filters'
import InventoryProduct from './Product.vue'
import InventoryTransferModal from './TransferModal.vue'
import KeyboardTypingOverlay from '@/components/keyboard/TypingOverlay.vue'
import modals from '@my/store/modals'
import { SCAN_SEPARATOR_REGEX, stripScanPrefix } from '@/helpers/scanCode'

export default {
  name: 'InventoryEdit',

  mixins: [mForm, mFilters],

  components: {
    InventoryProduct,
    InventoryTransferModal,
    KeyboardTypingOverlay
  },

  data () {
    return {
      brand: '',
      loaded: false,
      loadKey: 0,
      hiddenTiers: {},
      visibleTierOrder: [],
      transferSelection: null
    }
  },

  methods: {
    openTransferModal (tier, item) {
      this.transferSelection = { tier, item }
    },

    closeTransferModal () {
      this.transferSelection = null
    },

    onInventoryUpdated (payload = {}) {
      if (payload.targetTier) {
        this.showTier(this.resolveTierName(payload.targetTier))
      }

      this.syncTransferSelection()
    },

    resolveTierName (tier) {
      const group = this.findTierGroup(tier)
      return group ? group.tier : tier
    },

    findTierGroup (tier) {
      const normalized = String(tier).toLowerCase()
      return this.tierGroups.find(group => group.tier.toLowerCase() === normalized)
    },

    syncTransferSelection () {
      if (!this.transferSelection) return

      const { tier, item } = this.transferSelection
      const group = this.findTierGroup(tier)

      if (!group) {
        this.closeTransferModal()
        return
      }

      const found = group.items.find(entry => (
        entry.product === item.product && entry.size === item.size
      ))

      if (!found) {
        this.closeTransferModal()
        return
      }

      this.transferSelection = { tier, item: { ...found } }
    },

    async getData () {
      await this.$store.dispatch('getInventoryProducts', this.brand)
      this.hiddenTiers = {}
      this.visibleTierOrder = []

      this.tierGroups.forEach(group => {
        this.$set(this.hiddenTiers, group.tier, true)
      })

      this.loaded = true
      this.loadKey += 1
    },

    isTierVisible (tier) {
      const normalized = String(tier).toLowerCase()

      return !Object.entries(this.hiddenTiers).some(([key, hidden]) => (
        hidden && key.toLowerCase() === normalized
      ))
    },

    moveTierToTop (tier) {
      const normalized = String(tier).toLowerCase()
      const order = this.visibleTierOrder.filter(entry => entry.toLowerCase() !== normalized)
      order.unshift(this.resolveTierName(tier))
      this.visibleTierOrder = order
    },

    removeTierFromOrder (tier) {
      this.visibleTierOrder = this.visibleTierOrder.filter(t => t !== tier)
    },

    toggleTier (tier) {
      if (this.hiddenTiers[tier]) {
        this.$delete(this.hiddenTiers, tier)
        this.moveTierToTop(tier)
      } else {
        this.$set(this.hiddenTiers, tier, true)
        this.removeTierFromOrder(tier)
      }
    },

    showTier (tier) {
      const normalized = String(tier).toLowerCase()

      Object.keys(this.hiddenTiers).forEach(key => {
        if (key.toLowerCase() === normalized) {
          this.$delete(this.hiddenTiers, key)
        }
      })

      this.moveTierToTop(tier)
    },

    hideTier (tier) {
      this.$set(this.hiddenTiers, tier, true)
      this.removeTierFromOrder(tier)
    },

    hideAllTiers () {
      this.tierGroups.forEach(group => {
        this.$set(this.hiddenTiers, group.tier, true)
      })
      this.visibleTierOrder = []
    },

    getProductPrefix () {
      if (this.brand === 'sahet') return 'h'
      if (this.brand === 'bassica') return 'w'
      return null
    },

    getTierPrefix () {
      if (this.brand === 'sahet') return 't'
      if (this.brand === 'bassica') return 'b'
      return null
    },

    parseProductSearch (productReference) {
      const reference = productReference.toLowerCase()
      const separatorIndex = reference.search(SCAN_SEPARATOR_REGEX)

      if (separatorIndex === -1) {
        return { product: reference, size: null }
      }

      const product = reference.slice(0, separatorIndex)
      const size = reference.slice(separatorIndex + 1)

      if (!product || !size) {
        return { product: reference, size: null }
      }

      return { product, size: size === 'u' ? null : size }
    },

    findTiersByProduct (productReference) {
      const { product, size } = this.parseProductSearch(productReference)

      return this.tierGroups
        .filter(group => group.items.some(item => {
          if (item.product.toLowerCase() !== product) return false
          if (!size) return true
          return item.size.toLowerCase() === size
        }))
        .map(group => group.tier)
    },

    normalizeQuery (query) {
      return stripScanPrefix(query)
    },

    parseTierCode (value) {
      const trimmed = value.trim()

      if (!trimmed) return null

      const letter = trimmed.charAt(0).toUpperCase()
      const numberPart = trimmed.slice(1)

      if (!/^\d+$/.test(numberPart)) return null

      return { letter, number: parseInt(numberPart, 10) }
    },

    findTierMatch (query) {
      const parsedQuery = this.parseTierCode(query)
      if (!parsedQuery) return null

      const group = this.tierGroups.find(tierGroup => {
        const parsedTier = this.parseTierCode(tierGroup.tier)
        if (!parsedTier) return false

        return parsedTier.letter === parsedQuery.letter
          && parsedTier.number === parsedQuery.number
      })

      return group ? group.tier : null
    },

    revealTiers (tiers) {
      tiers.forEach(tier => this.showTier(tier))
    },

    isProductSearch (query) {
      const prefix = this.getProductPrefix()
      return prefix && query.startsWith(prefix)
    },

    handleProductSubmit (query) {
      const tiers = this.findTiersByProduct(query)
      this.revealTiers(tiers)
    },

    resolveTierFromQuery (query) {
      const parsed = this.parseTierCode(query)
      if (!parsed) return null

      const tierPrefix = this.getTierPrefix()
      if (!tierPrefix || parsed.letter.toLowerCase() !== tierPrefix) return null

      const normalized = `${tierPrefix}${parsed.number}`
      const fromVariables = (this.$store.state.variables.tiers || []).find(entry => (
        entry.toLowerCase() === normalized
      ))

      return fromVariables || normalized
    },

    ensureTierInStore (tier) {
      this.$store.commit('addTierGroup', { tier })
    },

    handleTierSubmit (query) {
      const parsed = this.parseTierCode(query)

      if (!parsed) return

      const tier = this.resolveTierFromQuery(query)

      if (!tier) {
        const expected = this.getTierPrefix()

        if (expected) {
          modals.showMessage({
            message: `El nivel debe empezar con "${expected}" para ${this.capitalize(this.brand)}.`,
            type: 'error'
          })
        }

        return
      }

      const existingGroup = this.findTierGroup(tier)

      if (!existingGroup) {
        this.ensureTierInStore(tier)
        this.showTier(tier)
        return
      }

      this.toggleTier(existingGroup.tier)
    },

    onTierSubmit (query) {
      const normalized = this.normalizeQuery(query)

      if (this.isProductSearch(normalized)) {
        this.handleProductSubmit(normalized)
        return
      }

      this.handleTierSubmit(normalized)
    }
  },

  computed: {
    tierGroups () {
      return this.$store.state.inventory.tierGroups
    },

    brands () {
      return this.$store.state.variables.brands
    },

    visibleTierGroups () {
      return this.visibleTierOrder
        .filter(tier => this.isTierVisible(tier))
        .map(tier => this.findTierGroup(tier))
        .filter(Boolean)
    },

    visibleTierNames () {
      return this.visibleTierGroups.map(group => group.tier)
    }
  }
}
</script>

<style lang="stylus" scoped>
.inventory-edit
  padding 0

.title-section
  font-size 1.3em
  font-weight 600
  margin-bottom 1em
  text-transform uppercase

.inventory-edit__form-row
  display flex
  align-items flex-end
  gap 1em
  flex-wrap nowrap
  width 100%
  margin-bottom 1em

  .input-container
    width auto
    flex 1
    min-width 0
    margin 0

.inventory-edit__form-actions
  flex-shrink 0

.inventory-edit__content
  margin-top 1em
  width 100%

.inventory-edit__hint
  font-size 1.2rem
  opacity .55
  text-align left
  margin 2em 0

.tiers-toolbar
  display flex
  align-items center
  flex-wrap wrap
  gap .5em
  margin-bottom 1em

.tier-tag
  display inline-flex
  align-items center
  gap .25em
  padding .25em .5em
  background-color #e9e9e7
  font-size 1em
  font-weight 600
  cursor default
  user-select none

.tier-tag__label
  line-height 1

.tier-tag__close
  font-size 1.2em
  cursor pointer
  opacity .55
  line-height 1

  &:hover
    opacity 1

.tier-groups
  position relative
  width 100%

.tier-group
  margin-top 2em
  margin-bottom 2em

.tier-header
  display flex
  align-items center
  gap .5em
  margin-bottom .8em

.tier-name
  font-size 1.5em
  font-weight 700

.tier-toggle
  font-size 2em
  cursor pointer
  opacity .45
  user-select none
  line-height 1

  &:hover
    opacity 1

.tier-items
  display flex
  flex-wrap wrap
  align-items flex-start
  gap 1.5em
  width 100%

  >>> .inventory-product
    flex 0 0 calc((100% - 3em) / 3)
    max-width calc((100% - 3em) / 3)
</style>



// WEBPACK FOOTER //
// src/components/pages/inventory/edit/Edit.vue